import { useState, type ReactNode } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Dialog } from "./Overlay";
import { InlineFeedback } from "./Feedback";

export type WizardStep = {
  key: string;
  title: string;
  summary?: string;
  content: ReactNode;
  /** 返回错误文案表示校验失败；返回 null 进入下一步。 */
  validate?: () => string | null;
};

/**
 * 唯一正式分步向导。创建和复杂修改使用向导；所有步骤保持挂载，
 * 步骤切换与校验失败都不清空已填内容。
 */
export function Wizard({ title, description, steps, submitLabel, onSubmit, onClose, submitting = false, submitError, submitDisabled = false, className = "", children }: {
  title: string;
  description: string;
  steps: WizardStep[];
  submitLabel: string;
  onSubmit: () => void | Promise<void>;
  onClose: () => void;
  submitting?: boolean;
  submitError?: string | null;
  /** 失败关闭：预检未通过/进行中/失败时禁止提交，只有真实通过才可点。 */
  submitDisabled?: boolean;
  className?: string;
  children?: ReactNode;
}) {
  const [stepIndex, setStepIndex] = useState(0);
  const [stepError, setStepError] = useState<string | null>(null);
  const step = steps[stepIndex];
  if (!step) return null;
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;

  function goNext() {
    if (!step?.validate) { setStepError(null); setStepIndex((index) => Math.min(index + 1, steps.length - 1)); return; }
    const failure = step.validate();
    if (failure) { setStepError(failure); return; }
    setStepError(null);
    setStepIndex((index) => Math.min(index + 1, steps.length - 1));
  }
  function goBack() { setStepError(null); setStepIndex((index) => Math.max(index - 1, 0)); }

  return <Dialog title={title} description={description} onClose={onClose} wide className={className}>
    <div className="wizard">
      <ol className="wizard-steps" aria-label="向导步骤">
        {steps.map((item, index) => <li
          key={item.key}
          className={index === stepIndex ? "current" : index < stepIndex ? "done" : ""}
          aria-current={index === stepIndex ? "step" : undefined}
        >
          <span className="wizard-step-index"><span className="app-number">{index < stepIndex ? "✓" : index + 1}</span></span>
          <span className="wizard-step-title">{item.title}</span>
        </li>)}
      </ol>
      {steps.map((item) => <div key={item.key} className="wizard-pane" hidden={item.key !== step.key} aria-hidden={item.key !== step.key}>
        {item.summary && item.key === step.key && <p className="wizard-summary">{item.summary}</p>}
        {item.content}
      </div>)}
      <div aria-live="polite">
        {stepError && <InlineFeedback tone="danger">{stepError}</InlineFeedback>}
        {isLast && submitError && <InlineFeedback tone="danger">{submitError}</InlineFeedback>}
        {isLast && children}
      </div>
      <footer className="dialog-footer">
        <button type="button" className="button secondary" onClick={onClose} disabled={submitting}>取消</button>
        <div className="wizard-nav">
          {!isFirst && <button type="button" className="button secondary" onClick={goBack} disabled={submitting}><ArrowLeft size={16} />上一步</button>}
          {isLast
            ? <button type="button" className="button primary" onClick={onSubmit} disabled={submitting || submitDisabled}>{submitting ? "正在提交…" : submitLabel}</button>
            : <button type="button" className="button primary" onClick={goNext}>下一步<ArrowRight size={16} /></button>}
        </div>
      </footer>
    </div>
  </Dialog>;
}
